/**
 * useNavCommands
 *
 * Builds the global navigation command list for the GlobalNavPalette.
 *
 * Commands are derived from URL params + cached resource queries (already
 * fetched by HeaderNavigation pills, so this is essentially free). Each
 * command knows its category and how to execute itself when invoked.
 *
 * Categories — display order (must match CATEGORY_ORDER below):
 *   workflows → open workflows in the current app
 *   apps      → switch apps in the current project
 *   projects  → switch projects in the current region
 *   pages     → context-aware page links (Workflows / Connections / …)
 *   account   → toggle theme, sign out
 *
 * Rationale: highest-frequency action (open workflow) first, then
 * context switches at progressively higher scope, then static pages,
 * then account at the bottom.
 *
 * The hook returns a flat list; the palette groups by category at render time.
 */

import { useMemo, type ComponentType, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DataUsage20Regular,
  Flow20Regular,
  PlugConnected20Regular,
  Settings20Regular,
  SignOut20Regular,
  SlideSettings20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { useProjects, useProviderLocations } from '@/hooks/useAzureResources';
import { useAppsInProject } from '@/hooks/useApps';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { paths } from '@/routes';
import { extractWorkflowName } from '@/types/logicApps';
import type { AutomationAppUIModel } from '@/types/automation';

export type NavCommandCategory =
  | 'pages'
  | 'projects'
  | 'apps'
  | 'workflows'
  | 'account';

export interface NavCommand {
  id: string;
  category: NavCommandCategory;
  /** Primary label shown in the row. */
  label: string;
  /** Optional muted secondary line (e.g. region for a project). */
  hint?: string | undefined;
  /** Search index — concatenated label + hint, lowercase. */
  searchText: string;
  /** Run the command. May navigate or perform a side effect. */
  execute: () => void;
  /** True when this command represents the currently-active location. */
  current?: boolean | undefined;
  /**
   * Optional icon override. When omitted, the palette falls back to a
   * category-based default. Used for commands whose icon needs to track
   * dynamic state (e.g. the theme toggle mirroring the profile menu's
   * sun/moon glyph).
   */
  icon?: ReactNode | undefined;
}

export const CATEGORY_LABELS: Record<NavCommandCategory, string> = {
  pages: 'Pages',
  projects: 'Switch project',
  apps: 'Switch app',
  workflows: 'Open workflow',
  account: 'Account',
};

export const CATEGORY_ORDER: NavCommandCategory[] = [
  'workflows',
  'apps',
  'projects',
  'pages',
  'account',
];

interface UseNavCommandsOptions {
  /** Whether to actually fetch resource queries — false when palette is closed. */
  enabled: boolean;
}

export function useNavCommands({ enabled }: UseNavCommandsOptions): NavCommand[] {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme, toggleTheme } = useAppStore();
  const params = useParams<{
    tenantId?: string;
    locationName?: string;
    projectName?: string;
    appName?: string;
    workflowName?: string;
  }>();
  const { tenantId, locationName, projectName, appName } = params;

  const { data: regions } = useProviderLocations();
  const { data: projects } = useProjects();
  const { data: apps } = useAppsInProject(enabled ? projectName ?? '' : '');

  // Synthesise URL-driven app context for workflow fetching, mirroring
  // HeaderNavigation: pulling from the Zustand store would race the URL
  // during navigation and surface stale workflows.
  const urlApp = useMemo(
    () =>
      enabled && appName && projectName
        ? ({ name: appName, projectName } as AutomationAppUIModel)
        : null,
    [enabled, appName, projectName],
  );
  const { data: workflows } = useAppWorkflows(urlApp);

  return useMemo<NavCommand[]>(() => {
    if (!enabled) return [];
    const cmds: NavCommand[] = [];

    // ── Pages — only meaningful once an app is selected ───────────────────
    // All `paths.app*` builders share the same signature, so we can drive
    // them off a single table instead of a switch.
    if (locationName && projectName && appName) {
      // Icons mirror the app sidebar (see appSidebarConfig.ts) so the
      // palette page rows match what users see in the side nav.
      const pageDefs: Array<{
        kind: string;
        label: string;
        icon: ComponentType;
        build: (loc: string, project: string, app: string, tid?: string | null) => string;
      }> = [
        { kind: 'workflows', label: 'Workflows', icon: Flow20Regular, build: paths.appWorkflows },
        { kind: 'connections', label: 'Connections', icon: PlugConnected20Regular, build: paths.appConnections },
        { kind: 'parameters', label: 'Parameters', icon: SlideSettings20Regular, build: paths.appParameters },
        { kind: 'analytics', label: 'Analytics', icon: DataUsage20Regular, build: paths.appAnalytics },
        { kind: 'settings', label: 'Settings', icon: Settings20Regular, build: paths.appSettings },
      ];
      for (const def of pageDefs) {
        const target = def.build(locationName, projectName, appName, tenantId);
        const Icon = def.icon;
        cmds.push({
          id: `page:${def.kind}`,
          category: 'pages',
          label: def.label,
          hint: `${projectName} › ${appName}`,
          searchText: `${def.label} ${appName}`.toLowerCase(),
          icon: <Icon />,
          execute: () => navigate(target),
        });
      }
    }

    // ── Projects in the current region ────────────────────────────────────
    if (locationName) {
      const regionLabel = regions?.find((r) => r.name === locationName)?.displayName ?? locationName;
      const projectsInRegion = projects?.filter((p) => p.location === locationName) ?? [];
      for (const project of projectsInRegion) {
        cmds.push({
          id: `project:${project.name}`,
          category: 'projects',
          label: project.name,
          hint: regionLabel,
          searchText: `${project.name} ${regionLabel}`.toLowerCase(),
          current: project.name === projectName,
          execute: () => navigate(paths.projectApps(locationName, project.name, tenantId)),
        });
      }
      // "View all projects in {region}" jump — mirrors the WorkspacePill shortcut.
      cmds.push({
        id: 'projects:list',
        category: 'projects',
        label: `View all projects in ${regionLabel}`,
        hint: `${projectsInRegion.length} ${projectsInRegion.length === 1 ? 'project' : 'projects'}`,
        searchText: `view all projects ${regionLabel}`.toLowerCase(),
        execute: () => navigate(paths.locationProjects(locationName, tenantId)),
      });
    }

    // ── Apps in the current project ───────────────────────────────────────
    if (locationName && projectName && apps) {
      const regionLabel = regions?.find((r) => r.name === locationName)?.displayName ?? locationName;
      for (const app of apps) {
        cmds.push({
          id: `app:${app.name}`,
          category: 'apps',
          label: app.name,
          hint: `${regionLabel} › ${projectName}`,
          searchText: `${app.name} ${projectName}`.toLowerCase(),
          current: app.name === appName,
          execute: () =>
            navigate(paths.appWorkflows(locationName, projectName, app.name, tenantId)),
        });
      }
      // "View all apps" jump — mirrors the AppPill shortcut.
      cmds.push({
        id: 'apps:list',
        category: 'apps',
        label: `View all apps in ${projectName}`,
        hint: `${apps.length} ${apps.length === 1 ? 'app' : 'apps'}`,
        searchText: `view all apps ${projectName}`.toLowerCase(),
        execute: () => navigate(paths.projectApps(locationName, projectName, tenantId)),
      });
    }

    // ── Workflows in the current app ──────────────────────────────────────
    if (locationName && projectName && appName && workflows) {
      const regionLabel = regions?.find((r) => r.name === locationName)?.displayName ?? locationName;
      for (const wf of workflows) {
        // Use the shared helper so prefix-stripping stays consistent with
        // WorkflowLeafPill and the rest of the app.
        const bare = extractWorkflowName(wf.name);
        cmds.push({
          id: `workflow:${bare}`,
          category: 'workflows',
          label: bare,
          hint: `${regionLabel} › ${projectName} › ${appName}`,
          searchText: `${bare} ${appName}`.toLowerCase(),
          execute: () =>
            navigate(
              paths.appWorkflowDesigner(locationName, projectName, appName, bare, tenantId),
            ),
        });
      }
    }

    // ── Account / preferences ─────────────────────────────────────────────
    cmds.push({
      id: 'theme:toggle',
      category: 'account',
      label: theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
      searchText: 'theme light dark mode appearance',
      // Mirror the profile dropdown's Theme item: moon while in dark mode,
      // sun while in light mode. Keeps the iconography consistent across
      // every entry point to the same action.
      icon: theme === 'dark' ? <WeatherMoon20Regular /> : <WeatherSunny20Regular />,
      execute: () => toggleTheme(),
    });
    cmds.push({
      id: 'auth:signout',
      category: 'account',
      label: 'Sign out',
      searchText: 'sign out logout log out',
      // Mirror the profile dropdown's Sign out item.
      icon: <SignOut20Regular />,
      execute: () => {
        void logout();
      },
    });

    return cmds;
  }, [
    enabled,
    appName,
    locationName,
    regions,
    projects,
    projectName,
    apps,
    workflows,
    tenantId,
    theme,
    navigate,
    toggleTheme,
    logout,
  ]);
}

/** Friendly subtitle shown next to a category header, mockup-style. */
export const CATEGORY_SUBTITLES: Record<NavCommandCategory, string | null> = {
  workflows: 'in this app',
  apps: 'in this project',
  projects: 'in this region',
  pages: null,
  account: null,
};
